import React, { useEffect, useMemo, useState } from "react";
import "./PartsSearch.css";

const FALLBACK_LIST_ID = "__bundled_g3wo__";

// Convert price strings like "1 3 0", "19 6", "4", "3*", "2 0*" to formatted WD currency.
const formatOldCurrency = (value) => {
  if (!value) return "";

  const text = String(value);
  const hasStar = text.includes("*");
  const cleaned = text.replace("*", "").trim();
  const parts = cleaned.split(/\s+/).map(Number);

  let pounds = 0;
  let shillings = 0;
  let pence = 0;

  if (parts.length === 3) {
    [pounds, shillings, pence] = parts;
  } else if (parts.length === 2) {
    [shillings, pence] = parts;
  } else if (parts.length === 1) {
    shillings = parts[0];
  }

  const out = [];

  if (pounds > 0) out.push(`£${pounds}`);
  if (shillings > 0 || pounds > 0) out.push(`${shillings}s`);
  if (pence > 0) out.push(`${pence}d`);

  if (out.length === 0) out.push("0d");
  if (hasStar) out.push("*");

  return out.join(" ");
};

const searchAMOC = (query) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = "https://www.amoc-parts.com/store/comersus_listItems.asp";
  form.target = "_blank";

  const qField = document.createElement("input");
  qField.type = "hidden";
  qField.name = "strSearch";
  qField.value = query;
  form.appendChild(qField);

  document.body.appendChild(form);
  form.submit();
  form.remove();
};

const extractNumericTail = (str) => {
  if (!str) return "";
  const parts = String(str).split("-");
  return parts[parts.length - 1];
};

const normalise = (str) => (str || "").toString().trim().toLowerCase();

const cleanSearchText = (value) =>
  String(value || "")
    .replace(/["“”]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const uniqueStrings = (values) => {
  const seen = new Set();

  return values
    .map(cleanSearchText)
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const quoteSearch = (value) => {
  const clean = cleanSearchText(value);
  return clean ? `"${clean}"` : "";
};

const buildGoogleSearchUrl = (query) =>
  `https://www.google.com/search?q=${encodeURIComponent(query)}`;

const buildEbayUkSearchUrl = (query) =>
  `https://www.ebay.co.uk/sch/i.html?_nkw=${encodeURIComponent(query)}`;

function stripLeadingPartPrefix(partNo) {
  return cleanSearchText(partNo).replace(/^[A-Za-z]+-/, "");
}

function extractUsefulPartSuffix(partNo) {
  const segments = cleanSearchText(partNo)
    .split("-")
    .map((segment) => segment.trim())
    .filter(Boolean);

  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const segment = segments[index];
    if (/^[A-Za-z]$/.test(segment)) continue;
    if (/\d/.test(segment) || segment.length > 1) return segment;
  }

  return "";
}

function buildPartNumberVariants(partNo) {
  const full = cleanSearchText(partNo);
  const withoutPrefix = stripLeadingPartPrefix(full);
  const suffix = extractUsefulPartSuffix(full);
  const spaced = full.replace(/-/g, " ");

  return uniqueStrings([full, withoutPrefix, suffix, spaced]);
}

function listModelText(model) {
  if (Array.isArray(model)) return model.filter(Boolean).join(" ");
  return cleanSearchText(model);
}

function buildBikeSearchContext(part, selectedList) {
  const make =
    cleanSearchText(part?.manufacturer || part?.make || selectedList?.manufacturer);
  const model = listModelText(part?.model || selectedList?.model);
  const year = cleanSearchText(part?.year || selectedList?.year);
  const bikeLabel = uniqueStrings([year, make, model]).join(" ");

  return { make, model, year, bikeLabel };
}

function joinQueryParts(parts) {
  return parts
    .map((part) =>
      String(part || "")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean)
    .join(" ");
}

function buildSmartPartSearchLinks(part, selectedList) {
  const partNo = cleanSearchText(part?.partNumber);
  const description = cleanSearchText(part?.description);
  const group = cleanSearchText(part?.group);
  const section = cleanSearchText(part?.section);
  const variants = buildPartNumberVariants(partNo);
  const suffix = extractUsefulPartSuffix(partNo);
  const withoutPrefix = stripLeadingPartPrefix(partNo);
  const { make, model, bikeLabel } = buildBikeSearchContext(part, selectedList);
  const makeModel = joinQueryParts([quoteSearch(make), quoteSearch(model)]);
  const groupSection = uniqueStrings([group, section]).join(" ");
  const partClause = variants.map(quoteSearch).filter(Boolean).join(" OR ");
  const primaryPart = partNo || withoutPrefix || suffix;
  const fallbackQuery = joinQueryParts([
    quoteSearch(bikeLabel || make),
    quoteSearch(description || groupSection || "classic motorcycle parts"),
  ]);

  const googleQuery = joinQueryParts([
    partClause ? `(${partClause})` : "",
    makeModel,
    quoteSearch(description),
    quoteSearch(groupSection),
  ]) || fallbackQuery;

  const ebayQuery = joinQueryParts([
    primaryPart || suffix,
    make,
    model,
    description,
  ]) || fallbackQuery;

  const jampotQuery = joinQueryParts([
    "site:jampot.com",
    quoteSearch(suffix || withoutPrefix || primaryPart),
    makeModel,
    quoteSearch(description || groupSection),
  ]) || fallbackQuery;

  const supplierQuery = joinQueryParts([
    "(site:amcclassicspares.com OR site:russellmotors.co.uk OR site:amoc-parts.com)",
    quoteSearch(suffix || withoutPrefix || primaryPart),
    makeModel,
    quoteSearch(description),
    quoteSearch(groupSection),
  ]) || fallbackQuery;

  return [
    {
      key: "google",
      label: "Google",
      href: buildGoogleSearchUrl(googleQuery),
      query: googleQuery,
    },
    {
      key: "ebay",
      label: "eBay UK",
      href: buildEbayUkSearchUrl(ebayQuery),
      query: ebayQuery,
    },
    {
      key: "jampot",
      label: "Jampot",
      href: buildGoogleSearchUrl(jampotQuery),
      query: jampotQuery,
    },
    {
      key: "suppliers",
      label: "Suppliers",
      href: buildGoogleSearchUrl(supplierQuery),
      query: supplierQuery,
    },
  ].filter((link) => link.query);
}

function formatListLabel(list) {
  if (!list) return "Select a bike";

  const model = Array.isArray(list.model) ? list.model.join(", ") : "";
  const bits = [list.year, list.manufacturer, model].filter(Boolean);
  return bits.length ? bits.join(" ") : list.fileName || "Parts list";
}

function flattenBundledParts(data) {
  if (!data || typeof data !== "object") return [];

  const rows = [];

  Object.entries(data).forEach(([groupName, sections]) => {
    if (!Array.isArray(sections)) return;

    sections.forEach((section) => {
      if (!section || !Array.isArray(section.items)) return;

      section.items.forEach((item) => {
        if (!item) return;

        rows.push({
          id: `${groupName}-${section.section || "section"}-${item.partNumber || rows.length}`,
          group: groupName,
          section: section.section || "",
          description: item.description || "",
          quantity: item.quantity ?? "",
          price: item.price ?? "",
          partNumber: item.partNumber || "",
          sourceFile: "Bundled G3WO parts JSON",
          visuals: [],
        });
      });
    });
  });

  return rows;
}

const PartsSearch = ({ data }) => {
  const [textQuery, setTextQuery] = useState("");
  const [textTokens, setTextTokens] = useState([]);
  const [partsLists, setPartsLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState("");
  const [remoteItems, setRemoteItems] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadError, setLoadError] = useState("");

  const fallbackItems = useMemo(() => flattenBundledParts(data), [data]);
  const fallbackList = useMemo(
    () =>
      fallbackItems.length
        ? {
            id: FALLBACK_LIST_ID,
            fileName: "Bundled G3WO parts JSON",
            manufacturer: "Matchless",
            model: ["G3WO"],
            year: 1940,
          }
        : null,
    [fallbackItems.length]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadLists() {
      setLoadingLists(true);
      setLoadError("");

      try {
        const res = await fetch("/.netlify/functions/parts-list-options");
        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load parts lists");
        }

        const nextLists = Array.isArray(data?.lists) ? data.lists : [];

        if (!cancelled) {
          setPartsLists(nextLists);
          setSelectedListId((current) => {
            if (current && nextLists.some((list) => list.id === current)) return current;
            if (nextLists[0]?.id) return nextLists[0].id;
            return fallbackList?.id || "";
          });
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || "Failed to load parts lists");
          setPartsLists([]);
          setSelectedListId(fallbackList?.id || "");
        }
      } finally {
        if (!cancelled) {
          setLoadingLists(false);
        }
      }
    }

    loadLists();

    return () => {
      cancelled = true;
    };
  }, [fallbackList]);

  useEffect(() => {
    let cancelled = false;

    async function loadItems() {
      if (!selectedListId || selectedListId === FALLBACK_LIST_ID) {
        setRemoteItems([]);
        return;
      }

      setLoadingItems(true);
      setLoadError("");

      try {
        const res = await fetch(
          `/.netlify/functions/parts-search?jobId=${encodeURIComponent(selectedListId)}`
        );
        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load parts for this bike");
        }

        if (!cancelled) {
          setRemoteItems(Array.isArray(data?.items) ? data.items : []);
        }
      } catch (err) {
        if (!cancelled) {
          setRemoteItems([]);
          setLoadError(err?.message || "Failed to load parts for this bike");
        }
      } finally {
        if (!cancelled) {
          setLoadingItems(false);
        }
      }
    }

    loadItems();

    return () => {
      cancelled = true;
    };
  }, [selectedListId]);

  const availableLists = useMemo(() => {
    return fallbackList ? [...partsLists, fallbackList] : partsLists;
  }, [fallbackList, partsLists]);

  const selectedList = useMemo(() => {
    return availableLists.find((list) => list.id === selectedListId) || null;
  }, [availableLists, selectedListId]);

  const allItems = selectedListId === FALLBACK_LIST_ID ? fallbackItems : remoteItems;

  const addToken = (raw) => {
    const token = raw.trim();
    if (!token) return;

    const lower = token.toLowerCase();
    setTextTokens((prev) => {
      if (prev.some((t) => t.toLowerCase() === lower)) return prev;
      return [...prev, token];
    });
    setTextQuery("");
  };

  const handleTextKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addToken(textQuery);
    }

    if (e.key === "Backspace" && !textQuery && textTokens.length > 0) {
      e.preventDefault();
      setTextTokens((prev) => prev.slice(0, prev.length - 1));
    }
  };

  const removeToken = (token) => {
    setTextTokens((prev) => prev.filter((t) => t !== token));
  };

  const clearAll = () => {
    setTextTokens([]);
    setTextQuery("");
  };

  const filteredItems = useMemo(() => {
    if (!allItems.length) return [];

    const baseTokens = textTokens.map(normalise);
    const liveToken = normalise(textQuery);
    const tokens = liveToken ? [...baseTokens, liveToken] : baseTokens;

    if (tokens.length === 0) return [];

    return allItems.filter((item) => {
      const combined = [
        item.group,
        item.section,
        item.description,
        item.partNumber,
        item.sourceFile,
        item.pageNumber,
        ...(Array.isArray(item.keywords) ? item.keywords : []),
      ]
        .map(normalise)
        .join(" ");

      return tokens.every((tok) => combined.includes(tok));
    });
  }, [allItems, textTokens, textQuery]);

  const hasAnyFilter = textTokens.length > 0 || textQuery.trim().length > 0;
  const tableItems = hasAnyFilter ? filteredItems : allItems.slice(0, 50);

  return (
    <div className="wd-parts-search card shadow-sm">
      <div className="card-body">
        <div className="wd-parts-search__header">
          <h2 className="wd-parts-search__title">Parts Search</h2>
          <p className="wd-parts-search__subtitle">
            Choose a bike, then search by description, section or part number.
          </p>
        </div>

        <div className="wd-parts-search__filters">
          <div className="wd-parts-search__field">
            <label className="wd-parts-search__label">Bike / parts list</label>
            <select
              className="form-control wd-parts-search__input wd-parts-search__select"
              value={selectedListId}
              onChange={(e) => {
                setSelectedListId(e.target.value);
                clearAll();
              }}
              disabled={loadingLists || availableLists.length === 0}
            >
              {availableLists.length === 0 ? (
                <option value="">No parts lists available</option>
              ) : (
                availableLists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {formatListLabel(list)}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="wd-parts-search__field wd-parts-search__field--wide">
            <label className="wd-parts-search__label">Search</label>

            <div className="wd-input-wrapper wd-input-wrapper--chips">
              <div className="wd-chip-row">
                {textTokens.map((token) => (
                  <span key={token} className="wd-chip">
                    <span className="wd-chip__text">{token}</span>
                    <button
                      type="button"
                      className="wd-chip__remove"
                      onClick={() => removeToken(token)}
                    >
                      x
                    </button>
                  </span>
                ))}

                <input
                  type="text"
                  className="form-control wd-parts-search__input wd-chip-input"
                  value={textQuery}
                  onChange={(e) => setTextQuery(e.target.value)}
                  onKeyDown={handleTextKeyDown}
                  placeholder={textTokens.length === 0 ? "Search parts..." : ""}
                  disabled={!selectedListId || loadingItems}
                />
              </div>

              {(textTokens.length > 0 || textQuery) && (
                <button className="wd-clear-btn wd-clear-btn--chips" onClick={clearAll}>
                  x
                </button>
              )}
            </div>
          </div>
        </div>

        {loadError ? <p className="wd-parts-search__no-results">{loadError}</p> : null}

        {!hasAnyFilter && (
          <p className="wd-parts-search__hint">
            {loadingLists || loadingItems
              ? "Loading parts..."
              : selectedList
              ? `${allItems.length} parts loaded from ${selectedList.fileName || "this parts list"}. Showing the first 50 until you search.`
              : "Upload and process a parts list in admin, then select it here."}
          </p>
        )}

        {hasAnyFilter && (
          <div className="wd-parts-search__summary">
            <span>
              {filteredItems.length > 0
                ? `${filteredItems.length} matching part${filteredItems.length !== 1 ? "s" : ""} found.`
                : "No matching parts found."}
            </span>
            <button type="button" className="wd-clear-all-btn" onClick={clearAll}>
              Clear
            </button>
          </div>
        )}

        {tableItems.length > 0 && (
          <div className="wd-parts-search__table-wrapper">
            <table className="table table-sm wd-parts-search__table">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Section</th>
                  <th>Description</th>
                  <th>Part No.</th>
                  <th>Search Web</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Page</th>
                  <th>Illustration</th>
                </tr>
              </thead>
              <tbody>
                {tableItems.map((item, idx) => {
                  const visual = Array.isArray(item.visuals) ? item.visuals[0] : null;
                  const webSearchLinks = buildSmartPartSearchLinks(item, selectedList);

                  return (
                    <tr key={`${item.id || item.partNumber}-${idx}`}>
                      <td>
                        <span className="wd-pill wd-pill--group">
                          {String(item.group || "")
                            .replace(/Section$/i, "")
                            .replace(/Section/i, "")
                            .replace(/_/g, " ") || "Parts"}
                        </span>
                      </td>
                      <td>
                        <span className="wd-pill wd-pill--section">
                          {item.section || "Parts list"}
                        </span>
                      </td>
                      <td>{item.description}</td>
                      <td>
                        {item.partNumber ? (
                          <button
                            className="wd-amoc-btn"
                            onClick={() => searchAMOC(extractNumericTail(item.partNumber))}
                            title="Search this part on AMOC Spares"
                          >
                            Search {item.partNumber}
                          </button>
                        ) : null}
                      </td>
                      <td>
                        {webSearchLinks.length > 0 ? (
                          <div className="wd-parts-search__web-links">
                            {webSearchLinks.map((link) => (
                              <a
                                key={link.key}
                                className="wd-web-search-link"
                                href={link.href}
                                target="_blank"
                                rel="noreferrer"
                                title={`Search for: ${link.query}`}
                              >
                                {link.label}
                              </a>
                            ))}
                          </div>
                        ) : null}
                      </td>
                      <td>{item.quantity}</td>
                      <td>{formatOldCurrency(item.price)}</td>
                      <td>{item.pageNumber || ""}</td>
                      <td>
                        {visual?.imageUrl ? (
                          <a
                            className="wd-parts-search__visual-link"
                            href={visual.imageUrl}
                            target="_blank"
                            rel="noreferrer"
                            title={visual.caption || visual.title || "Open illustration"}
                          >
                            <img
                              className="wd-parts-search__visual-thumb"
                              src={visual.imageUrl}
                              alt={visual.title || "Parts illustration"}
                              loading="lazy"
                            />
                            <span>Open</span>
                          </a>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {hasAnyFilter && filteredItems.length === 0 && (
          <p className="wd-parts-search__no-results">
            No matching parts found. Try removing a term or using something broader like{" "}
            <span className="wd-code">bolt</span> or{" "}
            <span className="wd-code">rear wheel</span>.
          </p>
        )}
      </div>
    </div>
  );
};

export default PartsSearch;
